import { DOCUMENT } from '@angular/common';
import {
  AfterContentInit,
  ContentChild,
  Directive,
  ElementRef,
  Inject,
  Input,
  OnDestroy,
} from '@angular/core';
import { FocusTarget, FocusTrap, Options, createFocusTrap } from 'focus-trap';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { Subscription } from 'rxjs';

/**
 * A directive built over [focus-trap](https://github.com/focus-trap/focus-trap) to trap focus
 * within a DOM node or [ngx-bootstrap](https://github.com/valor-software/ngx-bootstrap) modal.
 *
 * For an element with this directive applied:
 *
 *   (1) If it contains an ngx-bootstrap ModalDirective as a content child:
 *       (a) when the modal emits an `onShown` event the focus trap is created and activated.
 *       (b) when the modal emits an `onHidden` event, the focus trap is deactivated.
 *
 *   (2) If it does not contain an ngx-bootstrap ModalDirective as a content child, the focus
 *       trap will need to be manually activated and deactivated.
 *
 * Config options can be passed in via the input alias. Note that the config gets fixed at the
 * point of activation. Once the focus trap has been activated, the config can only be updated
 * by manually calling `this.activate()`. This is a limitation of focus-trap: config cannot be
 * changed on the fly, and so the focus trap must be replaced wholesale to update it.
 *
 * This directive can be applied to any element, but has been specifically created for and
 * applied to those which have the ngx-bootstrap [bsModal] directive applied.
 */
@Directive({
  selector: '[rlTrapFocus]',
  exportAs: 'rlTrapFocus',
})
export class TrapFocusDirective implements AfterContentInit, OnDestroy {
  @Input('rlTrapFocus') options?: Options = {};

  @ContentChild(ModalDirective) modal: ModalDirective;

  private focusTrap: FocusTrap;
  private subscriptions: Subscription[] = [];

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private elementRef: ElementRef,
  ) {}

  ngAfterContentInit() {
    if (!this.modal) {
      return;
    }

    this.subscriptions.push(
      this.modal.onShown.subscribe(({ isShown }) => {
        if (isShown) {
          this.activate();
        }
      }),
      this.modal.onHidden.subscribe(() => {
        this.deactivate();
      }),
    );
  }

  ngOnDestroy() {
    this.deactivate();
    this.subscriptions.forEach((subscription: Subscription) => {
      subscription.unsubscribe();
    });
    this.subscriptions = [];
  }

  /**
   * Create and activate the focus trap.
   *
   * To update any of the focus-trap config options after the initial activation,
   * call this method. This will deactivate the existing focus trap
   * and create a new one with the updated config.
   *
   * @returns {boolean} Whether activation was successful
   */
  public activate(): boolean {
    // Deactivate existing focus trap, if one exists
    this.deactivate();

    try {
      this.focusTrap = createFocusTrap(
        this.elementRef.nativeElement,
        this.options,
      );
      this.focusTrap.activate();
      return true;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.debug('Error activating focus trap', { error });
      return false;
    }
  }

  /**
   * Deactivate the focus trap.
   *
   * @returns {boolean} Whether deactivation was successful
   */
  public deactivate(): boolean {
    if (!this.focusTrap) {
      return true;
    }

    try {
      // If the `setReturnFocus` option has been provided at activation time,
      // check it points to an existing DOM node.
      if (
        this.options.setReturnFocus &&
        !this.nodeExists(this.options.setReturnFocus)
      ) {
        // If the `setReturnFocus` option is invalid, then do not attempt to return focus to it.
        // We do this since focus-trap will throw an error trying to return focus after deactivation,
        // which cannot be caught via our try/catch since it originates inside a `setTimeout`.
        this.focusTrap.deactivate({ returnFocus: false });
      } else {
        this.focusTrap.deactivate();
      }
      return true;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.debug('Error deactivating focus trap', { error });
      return false;
    }
  }

  /**
   * For a given focus target, check if a DOM node exists.
   *
   * @param {FocusTarget} focusTarget Focus target to validate
   * @returns {boolean}
   */
  private nodeExists(focusTarget: FocusTarget): boolean {
    switch (typeof focusTarget) {
      case 'string':
        return this.document.querySelector(focusTarget) ? true : false;
      case 'function':
        return focusTarget() ? true : false;
      default:
        return false;
    }
  }
}
